import { useMemo, useState } from 'react';
import { FormikProps, Formik } from 'formik';
import { Form, Input, Select } from 'formik-antd';
import * as Yup from 'yup';
import { Row, Col } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';

import {
  ContractedMBPServiceLinesEnum,
  ContractRenewalTermEnum,
  CustomerAgreementTypeEnum,
  IndemnificationTypeEnum,
  ResourceDownloadResponse,
} from 'api/adminApiClient';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import CertificateUploader from './CertificateUploader';
import Radio from 'components/formik/Radio';
import {
  mediumCharactersLength,
  assignmentRequirementLongTextMaxLength,
} from 'constants/index';
import SOWTemplateUploader from './SOWTemplateUploader';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countryCitiesSelector,
  countriesSelector,
  isCountryCitiesLoadingSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  citiesSelector,
  statesSelector,
  isStatesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import DatePicker from 'components/formik/DatePicker';
import {
  getContractedMBPServiceLinesOptions,
  getContractRenewalTermOptions,
  getCustomerAgreementTypeOptions,
  getIndemnificationTypeOptions,
} from 'utils/clientUtils';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<GeneralContractInfoFormValues>) => void;
  certificateFileInfo?: ResourceDownloadResponse;
  initialValues?: GeneralContractInfoFormValues;
  sowTemplateFileInfo?: ResourceDownloadResponse;
  isDeclined?: boolean;
};

const maxTextLength = 250;

export interface GeneralContractInfoFormValues {
  requiresCustomCOI?: boolean;
  submittedTo: string;
  certificate?: string[];
  contractSpecialInstructions: string;
  canContractBeAssigned?: boolean;
  useOfNameLogoPermitted?: boolean;
  customerAgreementType?: CustomerAgreementTypeEnum | null;
  governingLawJurisdiction?: string;
  indemnificationType?: IndemnificationTypeEnum | null;
  limitationOfLiability?: string;
  rightToInspectOrAudit?: boolean;
  contractedMBPServiceLines?: ContractedMBPServiceLinesEnum[];
  dataPrivacyRequirements?: boolean;
  dataPrivacyInstructions?: string;
  contractRenewalTerm?: ContractRenewalTermEnum | null;
  contractEffectiveDate?: string | null;
  contractExpirationDate?: string | null;
  noticePeriodInDays?: number | null;
  sowRequired?: boolean;
  sowSpecialInstructions: string;
  sowTemplate?: string[];
  declinationReason: string;
  attentionParty?: string;
  noticePartyCountryId?: number;
  noticePartyStateId?: number;
  noticePartyCity?: string;
  noticePartyInternationalCityName?: string;
  noticePartyInternationalStateName?: string;
  noticePartyAddress?: string;
  noticePartyZipCode?: string;
  noticePartyEmail?: string;
  noticePartyPhone?: string;
  noticePartyFaxNumber?: string;
  noticeMethod?: string;
}

const getValidationSchema = (isDeclined: boolean) =>
  Yup.object({
  requiresCustomCOI: Yup.boolean(),
  submittedTo: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Submitted to should be ${mediumCharactersLength} characters maximum`,
    ),
  certificate: Yup.array().of(Yup.string()),
  contractSpecialInstructions: Yup.string()
    .trim()
    .max(
      maxTextLength,
      `Contract special instructions should be ${maxTextLength} characters maximum`,
    ),
  canContractBeAssigned: Yup.boolean(),
  useOfNameLogoPermitted: Yup.boolean(),
  customerAgreementType: Yup.number().nullable(),
  governingLawJurisdiction: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Governing law jurisdiction should be ${mediumCharactersLength} characters maximum`,
    ),
  indemnificationType: Yup.number().nullable(),
  limitationOfLiability: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Limitation of liability should be ${mediumCharactersLength} characters maximum`,
    ),
  rightToInspectOrAudit: Yup.boolean(),
  contractedMBPServiceLines: Yup.array().of(Yup.number()),
  dataPrivacyRequirements: Yup.boolean(),
  dataPrivacyInstructions: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Data privacy instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  contractRenewalTerm: Yup.number().nullable(),
  contractEffectiveDate: Yup.string().nullable(),
  contractExpirationDate: Yup.string().nullable(),
  noticePeriodInDays: Yup.number()
    .nullable()
    .integer('Notice period should be a whole number')
    .min(0, 'Notice period should be greater than or equal to 0'),
  sowRequired: Yup.boolean(),
  sowSpecialInstructions: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `SOW special instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  sowTemplate: Yup.array().of(Yup.string()),
  declinationReason: isDeclined
    ? Yup.string()
        .trim()
        .max(
          assignmentRequirementLongTextMaxLength,
          `Declination reason should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
        )
        .required('Field is required')
    : Yup.string()
        .trim()
        .max(
          assignmentRequirementLongTextMaxLength,
          `Declination reason should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
        ),
  attentionParty: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Attention party should be ${mediumCharactersLength} characters maximum`,
    ),
  noticePartyCountryId: Yup.number().nullable(),
  noticePartyStateId: Yup.number().nullable(),
  noticePartyCity: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `City should be ${mediumCharactersLength} characters maximum`,
    ),
  noticePartyInternationalCityName: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `City should be ${mediumCharactersLength} characters maximum`,
    ),
  noticePartyInternationalStateName: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `State / province should be ${mediumCharactersLength} characters maximum`,
    ),
  noticePartyAddress: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Street should be ${mediumCharactersLength} characters maximum`,
    ),
  noticePartyZipCode: Yup.string()
    .trim()
    .max(50, 'ZIP code should be 50 characters maximum'),
  noticePartyEmail: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Email should be ${mediumCharactersLength} characters maximum`,
    )
    .test(
      'noticePartyEmail',
      'Must be a valid email',
      (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    ),
  noticePartyPhone: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Phone should be ${mediumCharactersLength} characters maximum`,
    ),
  noticePartyFaxNumber: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Fax number should be ${mediumCharactersLength} characters maximum`,
    ),
  noticeMethod: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Notice method should be ${mediumCharactersLength} characters maximum`,
    ),
});

export default function GeneralContractInfo(props: Props) {
  const {
    formRef,
    initialValues: predefinedInitialValues,
    certificateFileInfo,
    onChange,
    sowTemplateFileInfo,
    isDeclined,
  } = props;
  const [sowTemplateFile, setSowTemplateFile] = useState(sowTemplateFileInfo);
  const validationSchema = useMemo(
    () => getValidationSchema(!!isDeclined),
    [isDeclined],
  );

  const countries = useAppSelector(countriesSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isAdminRole = useAppSelector(isAdminSelector);

  const handleSowRequiredChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<GeneralContractInfoFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('sowTemplate', '', false);
      formik.setFieldValue('sowSpecialInstructions', '', false);
    }

    setSowTemplateFile(undefined);
  };

  const handleDataPrivacyChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<GeneralContractInfoFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('dataPrivacyInstructions', '', false);
    }
  };

  const initialValues: GeneralContractInfoFormValues = {
    requiresCustomCOI: undefined,
    submittedTo: '',
    certificate: [],
    contractSpecialInstructions: '',
    canContractBeAssigned: undefined,
    useOfNameLogoPermitted: undefined,
    customerAgreementType: null,
    governingLawJurisdiction: '',
    indemnificationType: null,
    limitationOfLiability: '',
    rightToInspectOrAudit: undefined,
    contractedMBPServiceLines: [],
    dataPrivacyRequirements: undefined,
    dataPrivacyInstructions: '',
    contractRenewalTerm: null,
    contractEffectiveDate: null,
    contractExpirationDate: null,
    noticePeriodInDays: null,
    sowRequired: undefined,
    sowSpecialInstructions: '',
    sowTemplate: [],
    declinationReason: '',
    attentionParty: '',
    noticePartyCountryId: undefined,
    noticePartyStateId: undefined,
    noticePartyCity: '',
    noticePartyInternationalCityName: '',
    noticePartyInternationalStateName: '',
    noticePartyAddress: '',
    noticePartyZipCode: '',
    noticePartyEmail: '',
    noticePartyPhone: '',
    noticePartyFaxNumber: '',
    noticeMethod: '',
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {(formik) => {
        const selectedCountry = formik.values.noticePartyCountryId;
        const selectedState = formik.values.noticePartyStateId;
        const useStatesDropdown = checkIsCountriesWithStatesDropdown(
          selectedCountry,
        );

        const filteredStates = states.filter(
          (state) => state.countryId === selectedCountry,
        );

        const filteredCities = cities.filter(
          (city) => city.stateId === selectedState,
        );

        const filteredCountryCities = countryCities.filter(
          (city) => city.countryId === selectedCountry,
        );

        const handleNoticePartyCountryChange = (countryId: number) => {
          formik.setFieldValue('noticePartyCountryId', countryId, false);
          formik.setFieldValue('noticePartyCity', '', false);
          formik.setFieldValue('noticePartyStateId', undefined, false);
          formik.setFieldValue('noticePartyInternationalCityName', '', false);
          formik.setFieldValue('noticePartyInternationalStateName', '', false);
        };

        const handleNoticePartyStateChange = (stateId: number) => {
          formik.setFieldValue('noticePartyStateId', stateId, false);
          formik.setFieldValue('noticePartyCity', '', false);
        };

        return (
          <>
            <FormikEffect onChange={onChange} />

            <Form layout="vertical">
              <div className="modal-title">Insurance requirements</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <FormItem
                    name="requiresCustomCOI"
                    label="Requires custom COI?"
                    data-id="editCustomerInfoRequiresCustomCOIField"
                    className="inline-radio"
                  >
                    <Radio.Group
                      className="radio"
                      name="requiresCustomCOI"
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="submittedTo"
                    label="Submitted to"
                    data-id="editCustomerInfoSubmittedToField"
                  >
                    <Input
                      name="submittedTo"
                      placeholder="Enter submitted to"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <div className="upload-wrapper">
                    <FormItem
                      name="certificate"
                      label="Certificate of insurance created"
                      data-id="editCustomerInfoCertificateField"
                      showValidateError={false}
                    >
                      <CertificateUploader
                        fieldName="certificate"
                        file={certificateFileInfo}
                      />
                    </FormItem>
                  </div>
                </Col>
              </Row>

              <div className="modal-title">Contract requirements</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <FormItem
                    name="canContractBeAssigned"
                    label="Can the contract be assigned?"
                    className="inline-radio"
                  >
                    <Radio.Group
                      className="radio"
                      name="canContractBeAssigned"
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="useOfNameLogoPermitted"
                    label="Use of name / logo permitted?"
                    className="inline-radio"
                  >
                    <Radio.Group
                      className="radio"
                      name="useOfNameLogoPermitted"
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="customerAgreementType"
                    label="Customer agreement type"
                  >
                    <Select
                      allowClear
                      name="customerAgreementType"
                      placeholder="Select agreement type"
                    >
                      {getCustomerAgreementTypeOptions().map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="governingLawJurisdiction"
                    label="Governing law jurisdiction"
                  >
                    <Input
                      name="governingLawJurisdiction"
                      placeholder="Enter governing law jurisdiction"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="indemnificationType"
                    label="Indemnification type"
                  >
                    <Select
                      allowClear
                      name="indemnificationType"
                      placeholder="Select indemnification type"
                    >
                      {getIndemnificationTypeOptions().map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="limitationOfLiability"
                    label="Limitation of liability"
                  >
                    <Input
                      name="limitationOfLiability"
                      placeholder="Enter limitation of liability"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="rightToInspectOrAudit"
                    label="Right to inspect or audit?"
                    className="inline-radio"
                  >
                    <Radio.Group
                      className="radio"
                      name="rightToInspectOrAudit"
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="contractedMBPServiceLines"
                    label="Contracted MBP service line(s)"
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      name="contractedMBPServiceLines"
                      placeholder="Select service lines"
                    >
                      {getContractedMBPServiceLinesOptions().map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="dataPrivacyRequirements"
                    label="Data privacy requirements"
                    className="inline-radio"
                  >
                    <Radio.Group
                      className="radio"
                      name="dataPrivacyRequirements"
                      onChange={(ev) => handleDataPrivacyChange(ev, formik)}
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                    />
                  </FormItem>
                </Col>

                {formik.values.dataPrivacyRequirements && (
                  <Col xs={24} md={12}>
                    <FormItem
                      name="dataPrivacyInstructions"
                      label="Data privacy instructions"
                    >
                      <Input
                        name="dataPrivacyInstructions"
                        placeholder="Enter data privacy instructions"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                )}

                <Col xs={24}>
                  <FormItem
                    name="contractRenewalTerm"
                    label="Contract renewal term"
                    className="inline-radio"
                  >
                    <Radio.Group name="contractRenewalTerm" className="radio">
                      {getContractRenewalTermOptions().map((opt) => (
                        <Radio
                          key={opt.value}
                          name={`contractRenewalTerm-${opt.value}`}
                          value={opt.value}
                        >
                          {opt.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="contractEffectiveDate"
                    label="Contract effective date"
                  >
                    <DatePicker
                      name="contractEffectiveDate"
                      format="MM/DD/YYYY"
                      placeholder="Select date"
                      className="full-width"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="contractExpirationDate"
                    label="Contract expiration date"
                  >
                    <DatePicker
                      name="contractExpirationDate"
                      format="MM/DD/YYYY"
                      placeholder="Select date"
                      className="full-width"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="noticePeriodInDays"
                    label="Notice period (in days)"
                  >
                    <InputNumber
                      name="noticePeriodInDays"
                      placeholder="Enter notice period"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="sowRequired"
                    label="SOW required"
                    data-id="editCustomerSOWRequiredField"
                    className="inline-radio"
                  >
                    <Radio.Group
                      className="radio"
                      name="sowRequired"
                      onChange={(ev) => handleSowRequiredChange(ev, formik)}
                      options={[
                        {
                          label: 'Yes',
                          value: true,
                        },
                        {
                          label: 'No',
                          value: false,
                        },
                      ]}
                    />
                  </FormItem>
                </Col>

                {formik.values.sowRequired && (
                  <>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="sowSpecialInstructions"
                        label="SOW special instructions"
                        data-id="editCustomerInfoSowSpecialInstructionsField"
                      >
                        <Input
                          name="sowSpecialInstructions"
                          placeholder="Enter SOW special instructions"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="upload-wrapper">
                        <FormItem
                          name="sowTemplate"
                          label="SOW template"
                          data-id="editCustomerSOWTemplateField"
                          showValidateError={false}
                        >
                          <SOWTemplateUploader
                            fieldName="sowTemplate"
                            file={sowTemplateFile}
                          />
                        </FormItem>
                      </div>
                    </Col>
                  </>
                )}

                <Col xs={24} md={12}>
                  <FormItem
                    name="contractSpecialInstructions"
                    label="Contract special instructions"
                    data-id="editCustomerContractSpecialInstructionsField"
                  >
                    <Input
                      name="contractSpecialInstructions"
                      placeholder="Enter contract special instructions"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                {isAdminRole && (
                  <Col xs={24}>
                    <FormItem
                      name="declinationReason"
                      label={
                        isDeclined ? 'Declination reason *' : 'Declination reason'
                      }
                    >
                      <Input.TextArea
                        name="declinationReason"
                        placeholder="Enter declination reason"
                        autoComplete="off"
                        rows={4}
                      />
                    </FormItem>
                  </Col>
                )}
              </Row>

              <div className="modal-title">Notice party</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <FormItem name="attentionParty" label="Attention party">
                    <Input
                      name="attentionParty"
                      placeholder="Enter attention party"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="noticePartyCountryId"
                    label="Country"
                    data-id="editCustomerNoticePartyCountryField"
                  >
                    <FormikSelectWithLoading
                      isLoading={isCountriesLoading}
                      name="noticePartyCountryId"
                      showSearch
                      placeholder="Select country"
                      optionFilterProp="children"
                      value={selectedCountry}
                      onChange={(value: number) =>
                        handleNoticePartyCountryChange(value)
                      }
                    >
                      {countries.map((country) => (
                        <FormikSelectWithLoading.Option
                          key={`notice-party-country-${country.id}`}
                          value={country.id || -1}
                        >
                          {country.name}
                        </FormikSelectWithLoading.Option>
                      ))}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>

                {useStatesDropdown ? (
                  <>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="noticePartyStateId"
                        label="State / province"
                        data-id="editCustomerNoticePartyStateField"
                      >
                        <FormikSelectWithLoading
                          isLoading={isStatesLoading}
                          name="noticePartyStateId"
                          showSearch
                          placeholder="Select state"
                          optionFilterProp="children"
                          value={selectedState}
                          onChange={(value: number) =>
                            handleNoticePartyStateChange(value)
                          }
                        >
                          {filteredStates.map((state) => (
                            <FormikSelectWithLoading.Option
                              key={`notice-party-state-${state.id}`}
                              value={state.id || -1}
                            >
                              {state.name}
                            </FormikSelectWithLoading.Option>
                          ))}
                        </FormikSelectWithLoading>
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        name="noticePartyCity"
                        label="City"
                        data-id="editCustomerNoticePartyCityField"
                      >
                        <FormikAutoCompleteWithLoading
                          isLoading={isCitiesLoading}
                          name="noticePartyCity"
                          filterOption
                          disabled={!formik.values.noticePartyStateId}
                          placeholder="Enter city"
                          showSearch
                        >
                          {filteredCities.map((city) => (
                            <FormikAutoCompleteWithLoading.Option
                              key={`notice-party-city-${city.id}`}
                              value={city.name || ''}
                            >
                              {city.name}
                            </FormikAutoCompleteWithLoading.Option>
                          ))}
                        </FormikAutoCompleteWithLoading>
                      </FormItem>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="noticePartyInternationalStateName"
                        label="State / province"
                      >
                        <Input
                          name="noticePartyInternationalStateName"
                          placeholder="Enter state"
                          disabled={!formik.values.noticePartyCountryId}
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        name="noticePartyInternationalCityName"
                        label="City"
                      >
                        <FormikAutoCompleteWithLoading
                          isLoading={isCountryCitiesLoading}
                          name="noticePartyInternationalCityName"
                          filterOption
                          disabled={!formik.values.noticePartyInternationalStateName}
                          placeholder="Enter city"
                          showSearch
                        >
                          {filteredCountryCities.map((city) => (
                            <FormikAutoCompleteWithLoading.Option
                              key={`notice-party-country-city-${city.id}`}
                              value={city.name || ''}
                            >
                              {city.name}
                            </FormikAutoCompleteWithLoading.Option>
                          ))}
                        </FormikAutoCompleteWithLoading>
                      </FormItem>
                    </Col>
                  </>
                )}

                <Col xs={24} md={12}>
                  <FormItem name="noticePartyAddress" label="Street">
                    <Input
                      name="noticePartyAddress"
                      placeholder="Enter street"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="noticePartyZipCode" label="ZIP code">
                    <Input
                      name="noticePartyZipCode"
                      placeholder="Enter ZIP code"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="noticePartyEmail" label="Email">
                    <Input
                      name="noticePartyEmail"
                      placeholder="Enter email"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="noticePartyPhone" label="Phone">
                    <Input
                      name="noticePartyPhone"
                      placeholder="Enter phone"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="noticePartyFaxNumber" label="Fax number">
                    <Input
                      name="noticePartyFaxNumber"
                      placeholder="Enter fax number"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="noticeMethod" label="Notice method">
                    <Input
                      name="noticeMethod"
                      placeholder="Enter notice method"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>
            </Form>
          </>
        );
      }}
    </Formik>
  );
}
